import type { AnalysisData } from '@/components/posture-analysis/types';
import type { FormValues, Analysis } from '@/lib/types';
import { updateClient } from '@/components/clients/services/clients-api';
import {
  computeClientClassificationDetail,
  computeClientClassificationDetailFromSop,
} from './client-classification';
import { deriveAndMergeClientNeeds } from './refresh-client-needs';
import logger from '@/lib/logging';

/**
 * Recompute and persist a client's short classification after a new/updated
 * artifact is saved, so the roster + profile badges never go stale. Also
 * auto-derives + additively merges needs tags from the new classification
 * (`deriveAndMergeClientNeeds`) — piggybacked here so it only ever runs on the
 * same recency-guarded call sites as the classification refresh itself. The
 * full OverallClassification (incl. movementLimitations) is passed through so
 * Biomechanical Intent / Movement Limitation needs derive too, not just the
 * Posture/Phase tags recoverable from the short string.
 *
 * Both helpers are failure-safe (swallow + log): a classification refresh must
 * never block or fail the underlying posture/SOP save. Callers should invoke
 * them fire-and-forget (`void refreshClientClassificationFromPosture(...)`).
 */
export async function refreshClientClassificationFromPosture(
  clientId: string,
  analysis: AnalysisData | null | undefined
): Promise<void> {
  try {
    const detail = await computeClientClassificationDetail(analysis);
    if (detail?.shortCode) {
      await updateClient(clientId, { classification: detail.shortCode });
      await deriveAndMergeClientNeeds(clientId, detail.shortCode, detail.overall);
    }
  } catch (error) {
    logger.warn(`Failed to refresh client classification from posture for ${clientId}`, error);
  }
}

export async function refreshClientClassificationFromSop(
  clientId: string,
  formValues: FormValues,
  analysis: Analysis | null,
  previousClassification?: string
): Promise<void> {
  try {
    const detail = await computeClientClassificationDetailFromSop(
      formValues,
      analysis,
      previousClassification
    );
    if (detail.shortCode) {
      await updateClient(clientId, { classification: detail.shortCode });
      await deriveAndMergeClientNeeds(clientId, detail.shortCode, detail.overall);
    }
  } catch (error) {
    logger.warn(`Failed to refresh client classification from SOP for ${clientId}`, error);
  }
}
